// @flow
import * as React from 'react'
import { useCallback } from 'react'
import type { Response } from 'types/response'
import type { SessionObject } from 'types/session-object'
import TextField from 'ui/molecules/TextField'

import { TriggerUserActions } from '@/components/TriggerUserActions'

import Flex from './atoms/Flex'
import { QuestionTitle } from './Text'

type Props = {
  response: Response,
  sessions?: SessionObject,
  showMiddleName?: boolean,
  block?: boolean,
  disabled?: boolean,
  errorLabel?: string,
  grayOutWhenDisabled?: boolean,
  hasError?: boolean,
  isOfficeUse?: boolean,
  isRequired?: boolean,
  label?: string | React.Node,
  name?: string,
  onBlur?: (e: SyntheticEvent<HTMLInputElement>) => void | Promise<void>,
  onChange?: (value: string) => void,
  placeholder?: string,
  style?: Object,
  value?: string,
  willShowNotAllowedCursor?: boolean,
}

function SingleLineName({
  showMiddleName,
  disabled,
  errorLabel,
  grayOutWhenDisabled,
  hasError,
  isOfficeUse,
  isRequired,
  label,
  onBlur,
  onChange = (value) => undefined,
  style,
  value,
  willShowNotAllowedCursor,
  questionId,
  response,
  sessions,
}: Props): React.Node {
  const [firstName, middleName, lastName] = React.useMemo(() => {
    const [firstName, middleName, lastName] = value?.split(':') ?? []
    return [firstName || '', middleName || '', lastName || '']
  }, [value])

  const handleChange = useCallback(
    (nextValue: string, field: string) => {
      if (field === 'firstName') {
        onChange(`${nextValue}:${middleName}:${lastName}`)
      }
      if (field === 'middleName') {
        onChange(`${firstName}:${nextValue}:${lastName}`)
      }
      if (field === 'lastName') {
        onChange(`${firstName}:${middleName}:${nextValue}`)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstName, middleName, lastName]
  )

  return (
    <div>
      {label && (
        <QuestionTitle isOfficeUse={isOfficeUse} isRequired={isRequired}>
          {label}
        </QuestionTitle>
      )}
      <Flex
        $flexDirection={{ default: 'col', lg: 'row', md: 'col', sm: 'col' }}
        $horizontalSpacing={{ default: 'none', lg: 'small' }}
        $verticalSpacing={{ default: 'small', lg: 'none' }}
      >
        <div className="w-full">
          <TriggerUserActions>
            <TextField
              data-form-id={response?.form?.id}
              data-packet-id={
                sessions?.fillResponseCustomer?.packetResponse?.packet?.id
              }
              data-response-id={response?.id}
              disabled={disabled}
              errorLabel={errorLabel}
              grayOutWhenDisabled={grayOutWhenDisabled}
              hasError={hasError && !firstName}
              id={`q-${questionId}`}
              isOfficeUse={isOfficeUse}
              isRequired={isRequired}
              name="firstName"
              onBlur={onBlur}
              onChange={(v) => handleChange(v, 'firstName')}
              placeholder="First Name"
              style={style}
              value={firstName}
              willShowNotAllowedCursor={willShowNotAllowedCursor}
            />
          </TriggerUserActions>
        </div>
        {showMiddleName && (
          <div className="w-full">
            <TextField
              disabled={disabled}
              grayOutWhenDisabled={grayOutWhenDisabled}
              isOfficeUse={isOfficeUse}
              name="middleName"
              onBlur={onBlur}
              onChange={(v) => handleChange(v, 'middleName')}
              placeholder="Middle Name"
              style={style}
              value={middleName}
              willShowNotAllowedCursor={willShowNotAllowedCursor}
            />
          </div>
        )}
        <div className="w-full">
          <TextField
            disabled={disabled}
            errorLabel={errorLabel}
            grayOutWhenDisabled={grayOutWhenDisabled}
            hasError={hasError && !lastName}
            isOfficeUse={isOfficeUse}
            isRequired={isRequired}
            name="lastName"
            onBlur={onBlur}
            onChange={(v) => handleChange(v, 'lastName')}
            placeholder="Last Name"
            style={style}
            value={lastName}
            willShowNotAllowedCursor={willShowNotAllowedCursor}
          />
        </div>
      </Flex>
    </div>
  )
}

export default SingleLineName
